// 請假申請 modal（leave-leave-form「請假申請」＝legacy `#dialogModal #dialogForm`，leave-fix 2026-10-04）。
//
// legacy 流程（行號親驗；ePortfolio_work/ePortfolio-tsgh/WebContent/WEB-INF/views/leave/leaveForm.jsp
// ＋ js-my/leaveForm.js ＋ student 擷取 .tmp/ui/raw-dom/legacy-modals/student/leave-form/
// leave-leave-form__add.html）：
//   - showForm(0,null)（leaveForm.js showForm）：標題「新增請假單」（:308），學員資料由
//     getNewLeaveFormDataAjax 回的 studentInfo 直填 label（setFillFormStudentInfo：姓名／
//     員編學號／學年度／訓練類別／訓練計畫／公務電話），**不是輸入欄**。
//   - 申請資料：假別 leaveTypeId（select2，請選擇＋leaveTypeList）＋請假須知；請假時間＝
//     起 sDate（datepicker readonly）＋sTime（select 00:00~23:00，顯示 0:00 值 08:00）、
//     迄 nDate＋nTime；時數計算 countDayHrLabel1/2（getLeaveDTO）；訓練科部 deptLabel；
//     到逹地點 destination（required）；佐證文件 docFile；說明 memo；簽核流程 flowTable
//     （順序／流程角色／填寫者）。
//   - saveNewForm(0)（:390-478）驗證鏈：dialogForm.valid()→『有必要欄位未填寫!』→
//     departmentId 空→『缺訓練科部!』→leaveHour 0→『錯誤!沒時數無法請假』或
//     `假別名(事前0日前提出申請)`→日期序 sdt==ndt『日期時間錯誤!』/sdt>ndt『日期時間錯誤!!』→
//     簽核欄位『有簽核流程欄位未填寫!』→$.confirm『確定要送申請?』→saveNewLeaveFormAjax
//     （FormData：id/accountId/jobTypeId/annualId/planId/organizationId/departmentId/
//     leaveHour/recessesHour/officeCode/contactPhone/leaveTypeId/destination/memo/signListStr/
//     startDate/endDate/status=0）→成功：modal hide＋doQuery＋showInfoDialog（:）。
//   - 送出後清單列 status=0＝「審核中」（constant.leaveFormStatus；leaveForm.jsp 查詢 select
//     全部/審核中/通過/未通過）。
//   - 檢視（viewForm）：同一 modal readonly（status>=0/-1 全部欄位 disabled，:322-327）。
//
// 為什麼不用 LegacyContractModal 的資料驅動版：leave-leave-form 的 dialogForm 是
// `shiro:hasRole Student` 限定（P20），admin 契約擷取看不到它，LEGACY_MODALS 沒有這筆規格
// （generated 檔不手改）；版面（學員資料 label 區／起~迄＋時段／簽核流程表／footer 雙鈕）
// 也不是「欄位清單」形狀。改走批次 10 editModal hook 先例（AccountV2DialogModal 同款），
// 並沿用 LegacyContractModals 的慣例：D-3 trigger 自報（editModalTriggerId）、
// 送出鈕 data-effect="create:<pageId>" 真打 DataSource（C5-j）、hidden 值一定進 payload、
// file 欄值取檔名。欄位 name 沿用 legacy（D-2）。
//
// 雛形策略【雛形值待實機校準】：學員資料種子＝legacy student 擷取的 seed 值（§C 對齊：
// 測試學生／ZZSTU-001／2026／系統管理／教學計畫審查作業（第 1 版）／管理部門）；
// 時數＝本機計算（起迄 datetime 差，整點小時）；課餘時間/已休/送審中依工作日曆＝0；
// 簽核流程＝Student 一列（seed §C FlowRole(plan,Student)＋LeaveTypeFlow×6 flowHours=0）。
// 日期欄 legacy 為 readonly＋datepicker（P46：fill 對 readonly 死等）——mock 無日期套件，
// 改可鍵入文字輸入（等價輸入 yyyy-MM-dd）。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { TextInput } from '@/components/base/TextInput';
import { FIELD_BASE_CLASS } from '@/components/base/shared-classes';
import { FormGrid, FormGridField, ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { LEAVE_TYPE_OPTIONS } from '@/mocks/resource-rules';
import { MockDataError, resolveLegacyMessage, useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';
import { FIELD_LABEL_CLASS } from '../templates/LegacyContractFields';
import { toUiKey } from '../templates/shared';

/** 學員資料種子（legacy student 擷取＋seed §C 對齊；DB 內部 id 為 seed 測試值）。 */
const STUDENT_INFO = {
  studentId: '6',
  studentName: '測試學生',
  employeeId: 'ZZSTU-001',
  year: '2026',
  annualId: '1',
  jobTypeId: '1',
  jobTypeName: '系統管理',
  planTargetId: '9110',
  planTargetName: '教學計畫審查作業（第 1 版）',
  organizationId: '1',
  phaseTargetDeptId: '1',
  phaseTargetDeptName: '管理部門',
} as const;

/** 假別規定時數上限（LeaveType.hours；seed §C 必修=8，其餘同值）。 */
const LEAVE_TYPE_MAX_HOURS = 8;

/** 時段下拉（leaveForm.jsp c:forEach 0~23：value 補前導零、顯示不補——jsp 顯示 8:00、值 08:00）。 */
const TIME_OPTIONS: readonly { value: string; label: string }[] = Array.from(
  { length: 24 },
  (_value, index) => ({ value: `${String(index).padStart(2, '0')}:00`, label: `${index}:00` }),
);

/** 時數計算（countDayHourInfo/getLeaveDTO 的 mock 版）：起迄 datetime 差取整點小時。 */
function leaveHoursOf(sDate: string, sTime: string, nDate: string, nTime: string): number {
  const start = Date.parse(`${sDate}T${sTime}:00Z`);
  const end = Date.parse(`${nDate}T${nTime}:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.max(0, Math.round((end - start) / 3_600_000));
}

/** 藍色區段標題（legacy inline style color:#0099ff 的「學員資料／申請資料」label）。 */
function SectionLabel({ uiId, text }: { uiId: string; text: string }) {
  return (
    <div data-ui-id={uiId} className="font-bold text-[#0099ff] md:col-span-2">
      {text}
    </div>
  );
}

/** 學員資料唯讀欄（legacy label 直填；P46：不是輸入欄，不可 fill）。 */
function StudentInfoField({
  uiId,
  label,
  value,
  span = 1,
}: {
  uiId: string;
  label: string;
  value: string;
  span?: 1 | 2;
}) {
  return (
    <div
      data-ui-id={uiId}
      className={`flex flex-col gap-1 ${span === 2 ? 'md:col-span-2' : 'md:col-span-1'}`}
    >
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <span
        data-ui-id={`${uiId}-value`}
        className="text-[16px] text-ink"
      >
        {value}
      </span>
    </div>
  );
}

/** 簽核流程表（legacy #flowTable：順序／流程角色／填寫者；Student 列填寫者＝登入者）。 */
function FlowTable({ uiId, visible }: { uiId: string; visible: boolean }) {
  if (!visible) return null;
  return (
    <div data-ui-id={uiId} className="mt-3 overflow-x-auto md:col-span-2">
      <table data-ui-id={`${uiId}-table`} className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <th data-ui-id={`${uiId}-th-0`} className="border-border border-b px-2 py-1 text-left">順序</th>
            <th data-ui-id={`${uiId}-th-1`} className="border-border border-b px-2 py-1 text-left">流程角色</th>
            <th data-ui-id={`${uiId}-th-2`} className="border-border border-b px-2 py-1 text-left">填寫者</th>
          </tr>
        </thead>
        <tbody>
          <tr data-ui-id={`${uiId}-row-1`}>
            <td data-ui-id={`${uiId}-row-1-seq`} className="border-border border-b px-2 py-1">1</td>
            <td data-ui-id={`${uiId}-row-1-role`} className="border-border border-b px-2 py-1">學員</td>
            <td data-ui-id={`${uiId}-row-1-signer`} className="border-border border-b px-2 py-1">
              {STUDENT_INFO.studentName}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export interface LeaveFormDialogModalProps {
  readonly pageId: string;
  /** 'create'＝請假申請（showForm(0,null)）；'edit'＝檢視（viewForm，列操作）。 */
  readonly mode: 'create' | 'edit';
  /** 檢視模式的列（create 時 null）。 */
  readonly row: Row | null;
  /** editing.values（PageBehavior.editModal 介面；create 的播種由本元件種子常數負責，不消費）。 */
  readonly values?: Record<string, string>;
  /** 關閉 modal（呼叫端 setEditing(null)）。 */
  readonly onClose: () => void;
  /** 關閉後刷新清單（legacy modal hide → doQuery）。 */
  readonly onClosed: () => void;
}

export function LeaveFormDialogModal({
  pageId,
  mode,
  row,
  onClose,
  onClosed,
}: LeaveFormDialogModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const uiId = `${pageId}-leave-dialog`;
  const isCreate = mode === 'create';

  // ------------------------------------------------------------------
  // 欄位值。create：學員資料為種子唯讀、申請欄位由使用者填；
  // edit（檢視）：從列回填（legacy setFormByRowData；mock 列只有表格欄位集＋
  // create 寫入的 legacy 鍵，缺的鍵空白【雛形值待實機校準】）。
  // ------------------------------------------------------------------
  const [officeCode, setOfficeCode] = useState('');
  const [contactPhone, setContactPhone] = useState(
    isCreate ? '' : String(row?.['contactPhone'] ?? ''),
  );
  const [leaveTypeId, setLeaveTypeId] = useState(isCreate ? '' : String(row?.['leaveTypeId'] ?? ''));
  const [sDate, setSDate] = useState(() => {
    if (isCreate) return '';
    const start = String(row?.['startDate'] ?? '');
    return start === '' ? '' : start.split(' ')[0] ?? '';
  });
  const [sTime, setSTime] = useState(() => {
    if (isCreate) return '00:00'; // legacy select 無 selected → 瀏覽器選首項 0:00
    const start = String(row?.['startDate'] ?? '');
    return start === '' ? '00:00' : start.split(' ')[1] ?? '00:00';
  });
  const [nDate, setNDate] = useState(() => {
    if (isCreate) return '';
    const end = String(row?.['endDate'] ?? '');
    return end === '' ? '' : end.split(' ')[0] ?? '';
  });
  const [nTime, setNTime] = useState(() => {
    if (isCreate) return '00:00';
    const end = String(row?.['endDate'] ?? '');
    return end === '' ? '00:00' : end.split(' ')[1] ?? '00:00';
  });
  const [destination, setDestination] = useState(isCreate ? '' : String(row?.['destination'] ?? ''));
  const [memo, setMemo] = useState(isCreate ? '' : String(row?.['memo'] ?? ''));
  const [docFileName, setDocFileName] = useState('');

  const hours = leaveHoursOf(sDate, sTime, nDate, nTime);
  const leaveTypeLabel =
    LEAVE_TYPE_OPTIONS.find((option) => option.value === leaveTypeId)?.label ?? '';
  // 簽核流程列（legacy setNewFlow：leaveTypeId 非空＋時數>0 才畫）。
  const flowVisible = leaveTypeId !== '' && hours > 0;
  // 學員資料區的顯示值：create＝種子（getNewLeaveFormDataAjax 回的 studentInfo）；
  // 檢視＝從列回填（legacy setFormByRowData；mock 列無 jobTypeName 鍵 → 空白
  // 【雛形值待實機校準】，與帳號大 modal 的「表單專屬欄位無種子值→空白」同款）。
  const studentView = isCreate
    ? {
        name: STUDENT_INFO.studentName,
        empId: STUDENT_INFO.employeeId,
        annual: STUDENT_INFO.year,
        jobType: STUDENT_INFO.jobTypeName,
        plan: STUDENT_INFO.planTargetName,
        dept: STUDENT_INFO.phaseTargetDeptName,
      }
    : {
        name: String(row?.['person'] ?? ''),
        empId: String(row?.['id'] ?? ''),
        annual: String(row?.['date'] ?? ''),
        jobType: String(row?.['jobTypeName'] ?? ''),
        plan: String(row?.['title'] ?? ''),
        dept: String(row?.['unit'] ?? ''),
      };

  // ------------------------------------------------------------------
  // payload（saveNewLeaveFormAjax FormData 鏡像＋mock 列欄映射）。
  // ------------------------------------------------------------------
  function buildPayload(): Row {
    const sdt = `${sDate} ${sTime}`;
    const ndt = `${nDate} ${nTime}`;
    return {
      // legacy dialogForm 隱藏欄＋表單欄（leaveForm.js saveNewForm FormData 逐欄；D-2 name）。
      // 不帶 `id`（見 hidden-fields 註解；mock row.id 由 create() 給號 LEA-114-9001+）。
      accountId: STUDENT_INFO.studentId,
      jobTypeId: STUDENT_INFO.jobTypeId,
      annualId: STUDENT_INFO.annualId,
      planId: STUDENT_INFO.planTargetId,
      organizationId: STUDENT_INFO.organizationId,
      departmentId: STUDENT_INFO.phaseTargetDeptId,
      leaveHour: String(hours),
      recessesHour: '0', // 課餘時間依工作日曆計算＝0【雛形值待實機校準】
      officeCode,
      contactPhone,
      leaveTypeId,
      destination,
      docFile: docFileName,
      memo,
      signListStr: JSON.stringify([
        { flowRole: 'Student', roleName: '學員', accountId: 6, accountName: STUDENT_INFO.studentName },
      ]),
      startDate: sdt,
      endDate: ndt,
      // mock 列欄（列表渲染；清單欄 key 由 legacy 表頭推斷，見 page-columns.ts 規則）。
      // status＝legacy codepoint 0 的顯示值「審核中」（constant.leaveFormStatus；
      // 查詢 select 值域 0/1/-1 與列顯示值的語意差＝W-1 §6 已登記的結構性差）。
      status: '審核中',
      date: STUDENT_INFO.year,
      title: STUDENT_INFO.planTargetName,
      person: STUDENT_INFO.studentName,
      unit: STUDENT_INFO.phaseTargetDeptName,
      category: leaveTypeLabel,
      date2: `${sdt} ~ ${ndt}`, // legacy 清單 render：startDatetimeStr + ' ~ <br>' + endDatetimeStr
      score: String(hours),
      text: `${STUDENT_INFO.studentName}(學員)`, // legacy 簽核流程欄：signerName(signerTitle)
    };
  }

  // ------------------------------------------------------------------
  // 送出（saveNewForm(0) 驗證鏈逐條）
  // ------------------------------------------------------------------
  const submit = () => {
    // ① docFile required（needEvidence）——mock 假別種子皆無 needEvidence → 不觸發（seed §C）。
    // ② dialogForm.valid()：leaveTypeId/sDate/sTime/nDate/nTime/destination required。
    if (
      leaveTypeId === '' ||
      sDate === '' ||
      sTime === '' ||
      nDate === '' ||
      nTime === '' ||
      destination.trim() === ''
    ) {
      void dialogs.alert('有必要欄位未填寫!');
      return;
    }
    // ③ departmentId 空 →『缺訓練科部!』——種子恆帶 phaseTargetDeptId → 不觸發（保留語意註記）。
    // ④ leaveHour 0 →『錯誤!沒時數無法請假』；noticeDay=0 → `假別名(事前0日前提出申請)`。
    if (hours === 0) {
      void dialogs.alert(
        leaveTypeLabel === '' ? '錯誤!沒時數無法請假' : `${leaveTypeLabel}(事前0日前提出申請)`,
      );
      return;
    }
    const sdt = `${sDate} ${sTime}`;
    const ndt = `${nDate} ${nTime}`;
    // ⑤ 日期序。
    if (sdt === ndt) {
      void dialogs.alert('日期時間錯誤!');
      return;
    }
    if (sdt > ndt) {
      void dialogs.alert('日期時間錯誤!!');
      return;
    }
    // ⑥ 簽核流程欄位（getVerifierFlowList null）——Student 列帶登入者，mock 恆成立 → 不觸發。
    // ⑦ $.confirm『確定要送申請?』→ 送出。
    void (async () => {
      const confirmed = await dialogs.confirm('確定要送申請?');
      if (!confirmed) return;
      try {
        await dialogs.processing.wrap(dataSource.create(pageId, buildPayload()));
        onClose();
        onClosed();
        await dialogs.info(resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
      }
    })();
  };

  const readOnly = !isCreate; // 檢視模式全欄位 disabled（legacy status>=0/-1 同款）
  // 批次 50（工作項 4）：uiId 字尾一律 toUiKey kebab 化（§10.3 規則 1；name 參數本身
  // 不動＝name 屬性/payload key 域零影響；現有呼叫 sDate/sTime/nDate/nTime/destination
  // 全小寫已合規，包裝為防禦性一致化）。
  const input = (name: string) => `${uiId}-input-${toUiKey(name)}`;

  return (
    <ModalFrame
      uiId={uiId}
      open
      title={isCreate ? '新增請假單' : '檢視請假單'}
      size="lg"
      onClose={onClose}
      footer={
        isCreate ? (
          <>
            {/* 批次 48 uiId 衛生：ModalFrame 的 × 鈕已佔 `${uiId}-close`，footer 關閉鈕同名會造成
                同 uiId 雙元素——extract-modals 關閉步驟以 `${target}-close` strict mode 單元素點擊
                （extract-modals.spec.ts:209），雙元素必炸 strict mode 並 serial 取消後續頁
                （批次 47 全量 modals 管線繞不過的根因）。footer 改 `-close-footer`；文案與行為不變。 */}
            <Button
              uiId={`${uiId}-close-footer`}
              variant="default"
              size="sm"
              type="button"
              onClick={onClose}
            >
              {t('action.close')}
            </Button>
            <Button
              uiId={`${uiId}-submit`}
              variant="primary"
              size="sm"
              type="button"
              // D-1／criteria C5-j：真的呼叫 dataSource.create（LegacyContractModals 慣例）。
              dataEffect={`create:${pageId}`}
              onClick={submit}
            >
              送出申請
            </Button>
          </>
        ) : (
          // 同上：檢視模式的 footer 關閉鈕也不得與 ModalFrame × 鈕同名（批次 48 uiId 衛生）。
          <Button
            uiId={`${uiId}-close-footer`}
            variant="default"
            size="sm"
            type="button"
            onClick={onClose}
          >
            {t('action.close')}
          </Button>
        )
      }
    >
      <div data-ui-id={`${uiId}-hidden-fields`} className="hidden">
        {/* legacy dialogForm 隱藏欄鏡像（值一定進 payload；無使用者改值事件）。
            唯獨不鏡像 `id`：legacy FormData 的 id=0 是 DB PK，mock 的 row.id 是新列
            給號（create() → LEA-114-9001+），payload 帶 id 會被當成使用者帶值 id
            （crud-fix P35 反轉的 create() 語意）寫成列鍵，故不送。 */}
        <input type="hidden" name="accountId" data-ui-id={input('accountId')} value={STUDENT_INFO.studentId} readOnly />
        <input type="hidden" name="jobTypeId" data-ui-id={input('jobTypeId')} value={STUDENT_INFO.jobTypeId} readOnly />
        <input type="hidden" name="annualId" data-ui-id={input('annualId')} value={STUDENT_INFO.annualId} readOnly />
        <input type="hidden" name="planId" data-ui-id={input('planId')} value={STUDENT_INFO.planTargetId} readOnly />
        <input type="hidden" name="organizationId" data-ui-id={input('organizationId')} value={STUDENT_INFO.organizationId} readOnly />
        <input type="hidden" name="departmentId" data-ui-id={input('departmentId')} value={STUDENT_INFO.phaseTargetDeptId} readOnly />
        <input type="hidden" name="leaveHour" data-ui-id={input('leaveHour')} value={String(hours)} readOnly />
        <input type="hidden" name="recessesHour" data-ui-id={input('recessesHour')} value="0" readOnly />
      </div>
      <FormGrid uiId={`${uiId}-grid`} columns={2}>
        <SectionLabel uiId={`${uiId}-section-student`} text="學員資料" />
        <StudentInfoField uiId={`${uiId}-label-name`} label="姓名：" value={studentView.name} />
        <StudentInfoField uiId={`${uiId}-label-empid`} label="員編/學號：" value={studentView.empId} />
        <StudentInfoField uiId={`${uiId}-label-annual`} label="學年度：" value={studentView.annual} />
        <StudentInfoField uiId={`${uiId}-label-jobtype`} label="訓練類別：" value={studentView.jobType} />
        <StudentInfoField
          uiId={`${uiId}-label-plan`}
          label="訓練計畫："
          value={studentView.plan}
          span={2}
        />
        <FormGridField uiId={`${uiId}-field-office-code`} label="公務電話：">
          <TextInput
            uiId={input('officeCode')}
            name="officeCode"
            value={officeCode}
            maxLength={20}
            disabled={readOnly}
            onChange={setOfficeCode}
          />
        </FormGridField>
        <FormGridField uiId={`${uiId}-field-contact-phone`} label="聯絡電話：">
          <TextInput
            uiId={input('contactPhone')}
            name="contactPhone"
            value={contactPhone}
            maxLength={20}
            disabled={readOnly}
            onChange={setContactPhone}
          />
        </FormGridField>

        <SectionLabel uiId={`${uiId}-section-apply`} text="申請資料" />
        <FormGridField uiId={`${uiId}-field-leave-type-id`} label="假別：">
          <div className="flex items-start gap-3">
            <div className="w-48">
              <Select
                uiId={input('leaveTypeId')}
                name="leaveTypeId"
                value={leaveTypeId}
                placeholder="請選擇"
                required
                disabled={readOnly}
                options={LEAVE_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                onChange={setLeaveTypeId}
              />
            </div>
            <div data-ui-id={`${uiId}-leave-type-notice`} className="text-[13px]">
              <span className="font-bold text-[#ff0000]">請假須知：</span>
              {/* LeaveType.notice 種子無內容（seed §C）→ 空，與 legacy 擷取一致 */}
            </div>
          </div>
        </FormGridField>
        {leaveTypeId !== '' && sDate !== '' && nDate !== '' ? (
          <div data-ui-id={`${uiId}-leave-desc`} className="text-[13px] md:col-span-2">
            {`規定時數:${LEAVE_TYPE_MAX_HOURS}小時，已休時數:0小時，送審中時數:0小時，剩餘可休時數:${LEAVE_TYPE_MAX_HOURS}小時`}
          </div>
        ) : null}
        <FormGridField uiId={`${uiId}-field-start`} label="請假時間：起">
          <div className="flex items-center gap-2">
            <div className="w-40">
              {/* legacy readonly＋datepicker（P46）→ mock 可鍵入文字（等價輸入 yyyy-MM-dd）。 */}
              <TextInput
                uiId={input('sDate')}
                name="sDate"
                value={sDate}
                maxLength={10}
                required
                disabled={readOnly}
                onChange={setSDate}
              />
            </div>
            <div className="w-28">
              <Select
                uiId={input('sTime')}
                name="sTime"
                value={sTime}
                required
                disabled={readOnly}
                options={TIME_OPTIONS}
                onChange={setSTime}
              />
            </div>
          </div>
        </FormGridField>
        <FormGridField uiId={`${uiId}-field-end`} label="迄">
          <div className="flex items-center gap-2">
            <div className="w-40">
              <TextInput
                uiId={input('nDate')}
                name="nDate"
                value={nDate}
                maxLength={10}
                required
                disabled={readOnly}
                onChange={setNDate}
              />
            </div>
            <div className="w-28">
              <Select
                uiId={input('nTime')}
                name="nTime"
                value={nTime}
                required
                disabled={readOnly}
                options={TIME_OPTIONS}
                onChange={setNTime}
              />
            </div>
          </div>
        </FormGridField>
        {hours > 0 ? (
          <div data-ui-id={`${uiId}-count-hour`} className="text-[13px] md:col-span-2">
            <span data-ui-id={`${uiId}-count-total`}>{`合計:${hours}小時`}</span>
            <span data-ui-id={`${uiId}-count-recess`} className="ml-6">課餘時間:0小時</span>
          </div>
        ) : null}
        <FormGridField uiId={`${uiId}-field-dept`} label="訓練科部：">
          <div className="flex items-center gap-6 text-[16px] text-ink">
            <span data-ui-id={`${uiId}-label-dept`}>{studentView.dept}</span>
            {hours > 0 ? (
              <span data-ui-id={`${uiId}-label-dept-hour`} className="text-[13px]">
                {`已休時數：0小時  ， 送審中時數：0小時`}
              </span>
            ) : null}
          </div>
        </FormGridField>
        <FormGridField uiId={`${uiId}-field-destination`} label="到逹地點：" required>
          <TextInput
            uiId={input('destination')}
            name="destination"
            value={destination}
            maxLength={200}
            required
            disabled={readOnly}
            onChange={setDestination}
          />
        </FormGridField>
        <FormGridField uiId={`${uiId}-field-doc-file`} label="佐證文件：">
          {/* 上傳型：值取檔名（C5-d file 慣例）；mock 無檔案服務。 */}
          <input
            type="file"
            name="docFile"
            data-ui-id={input('docFile')}
            className={FIELD_BASE_CLASS}
            disabled={readOnly}
            onChange={(event) => {
              setDocFileName(event.target.files?.[0]?.name ?? event.target.value);
            }}
          />
        </FormGridField>
        <FormGridField uiId={`${uiId}-field-memo`} label="說明：" span={2}>
          <textarea
            name="memo"
            data-ui-id={input('memo')}
            rows={4}
            maxLength={200}
            disabled={readOnly}
            className={FIELD_BASE_CLASS}
            value={memo}
            onChange={(event) => {
              setMemo(event.target.value);
            }}
          />
        </FormGridField>
        <h3 className="font-bold md:col-span-2">簽核流程：</h3>
        <FlowTable uiId={`${uiId}-flow`} visible={isCreate ? flowVisible : false} />
      </FormGrid>
    </ModalFrame>
  );
}
